import React, { useEffect, useState } from "react";
import { X, Sparkles, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi } from "./adminApi";

/**
 * Admin / C-ORE Expert — Business Context + "Inject Task into Command
 * Centre" (2026-09). Reuses the existing Command Center task system —
 * this modal only reads existing business data and, on submit, creates a
 * normal `founder_tasks` row (via /admin/expert/inject-task) tagged
 * source_type=admin_expert + the chosen executive_role. The customer sees
 * it exactly like any other Command Center task — never an "Admin" label.
 */
const NAVY = "#1E3A5F";
const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16,
};
const card = {
  background: "white", borderRadius: 14, width: "100%", maxWidth: 620,
  maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif",
};
const input = { width: "100%", boxSizing: "border-box", border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 10px", fontSize: 13, color: "#0F1B2D" };
const label = { display: "block", fontSize: 11.5, color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };
const btn = { border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const sectionTitle = { fontSize: 12.5, fontWeight: 800, color: "#0F1B2D", marginBottom: 8, marginTop: 18, textTransform: "uppercase", letterSpacing: 0.5 };
const kv = { display: "flex", gap: 6, fontSize: 12.5, color: "#374151", marginBottom: 3 };
const kvLabel = { color: "#9CA3AF", minWidth: 130 };

const ROLE_COLORS = {
  CEO: { bg: "#EFF6FF", fg: "#1D4ED8" },
  CFO: { bg: "#ECFDF5", fg: "#047857" },
  CMO: { bg: "#FFF7ED", fg: "#C2410C" },
  CTO: { bg: "#F5F3FF", fg: "#7C3AED" },
};
const PRIORITY_OPTIONS = [
  { value: 1, label: "High" },
  { value: 3, label: "Medium" },
  { value: 5, label: "Low" },
];

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}
function fmtCr(rupees) {
  if (rupees === null || rupees === undefined || rupees === "") return "—";
  return `₹${(Number(rupees) / 1_00_00_000).toFixed(2)} Cr`;
}

export default function CustomerContextModal({ customer, onClose }) {
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [view, setView] = useState("context"); // context | form
  const [form, setForm] = useState({
    title: "", description: "", businessObjective: "", expectedOutcome: "",
    executiveRole: "CEO", priority: 3, dueDate: "", internalNote: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [duplicates, setDuplicates] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await adminApi.expertCustomerContext(customer.id);
      if (!alive) return;
      if (res.ok) setCtx(res.data);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [customer.id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (force = false) => {
    if (!form.title.trim()) { setErr("Please enter a task title."); return; }
    if (!form.description.trim()) { setErr("Please enter a task description."); return; }
    setBusy(true); setErr(""); setDuplicates(null);
    const res = await adminApi.expertInjectTask({
      customer_id: customer.id,
      title: form.title.trim(),
      description: form.description.trim(),
      business_objective: form.businessObjective.trim() || null,
      expected_outcome: form.expectedOutcome.trim() || null,
      executive_role: form.executiveRole,
      priority: form.priority,
      due_date: form.dueDate || null,
      internal_expert_note: form.internalNote.trim() || null,
      force,
    });
    setBusy(false);
    if (!res.ok) { setErr(res.error || "Could not inject this task. Please try again."); return; }
    if (res.data?.duplicate_warning) { setDuplicates(res.data.similar_tasks || []); return; }
    setResult(res.data);
  };

  const name = customer.company_name || customer.name || customer.id;
  const bp = ctx?.business_profile || {};
  const cc = ctx?.command_center || {};
  const target = ctx?.targets?.strategic_target;

  return (
    <div style={overlay} data-testid="customer-context-modal" onClick={() => !busy && onClose()}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #E5E7EB", position: "sticky", top: 0, background: "white" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {view === "form" && !result && (
              <button data-testid="context-back-btn" onClick={() => { setView("context"); setErr(""); setDuplicates(null); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6B7280", display: "flex" }}>
                <ArrowLeft size={16} />
              </button>
            )}
            <Sparkles size={16} color={NAVY} />
            <span style={{ fontWeight: 800, fontSize: 15, color: "#0F1B2D" }}>
              {view === "context" ? "Business Context" : "Inject Task into Command Centre"}
            </span>
          </div>
          <button data-testid="context-close-btn" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 10 }}>
            <b>{name}</b> {customer.email ? <span style={{ color: "#6B7280" }}>· {customer.email}</span> : null}
          </div>

          {loading && <div style={{ fontSize: 13, color: "#6B7280", padding: 24, textAlign: "center" }}>Loading business context…</div>}

          {!loading && view === "context" && (
            <div data-testid="context-view">
              <div style={sectionTitle}>Company Profile</div>
              <div style={kv}><span style={kvLabel}>Industry</span><span>{fmt(ctx?.customer?.industry)}</span></div>
              <div style={kv}><span style={kvLabel}>Geography</span><span>{fmt(ctx?.customer?.city)}{ctx?.customer?.state ? `, ${ctx.customer.state}` : ""}</span></div>
              <div style={kv}><span style={kvLabel}>Revenue range</span><span>{fmt(ctx?.customer?.revenue_range)}</span></div>
              <div style={kv}><span style={kvLabel}>Employees</span><span>{fmt(ctx?.customer?.employee_range)}</span></div>
              <div style={kv}><span style={kvLabel}>Business model</span><span>{fmt(bp.primary_business_model)}{bp.modifier_flags?.b2b ? " · B2B" : ""}{bp.modifier_flags?.b2c ? " · B2C" : ""}</span></div>
              <div style={kv}><span style={kvLabel}>Software used</span><span>{Array.isArray(ctx?.customer?.software_used) ? ctx.customer.software_used.join(", ") : fmt(ctx?.customer?.software_used)}</span></div>

              <div style={sectionTitle}>Business Objectives &amp; Targets</div>
              <div style={kv}><span style={kvLabel}>Revenue target</span><span>{fmtCr(ctx?.targets?.revenue_target)}</span></div>
              {target ? (
                <div style={kv}><span style={kvLabel}>Strategic target</span>
                  <span>₹{target.target_value} Cr in {target.horizon_years}yr{target.focus ? ` · ${target.focus}` : ""}{target.target_fy ? ` · ${target.target_fy}` : ""}</span>
                </div>
              ) : (
                <div style={kv}><span style={kvLabel}>Strategic target</span><span style={{ color: "#9CA3AF" }}>No strategic target set yet.</span></div>
              )}

              <div style={sectionTitle}>Current Business State</div>
              <div style={kv}><span style={kvLabel}>Last FY revenue</span><span>{fmtCr(ctx?.current_state?.revenue_last_fy_actual)}</span></div>
              <div style={kv}><span style={kvLabel}>Marketing spend</span><span>{fmt(ctx?.current_state?.marketing_spend)}</span></div>
              <div style={kv}><span style={kvLabel}>IT spend / month</span><span>{fmt(ctx?.current_state?.it_spend_monthly)}</span></div>
              <div style={kv}><span style={kvLabel}>NPS score</span><span>{fmt(ctx?.current_state?.nps_score)}</span></div>

              <div style={sectionTitle}>Current Command Centre</div>
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <span data-testid="context-cc-open" style={{ background: "#F3F4F6", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>Open: <b>{cc.open_items ?? 0}</b></span>
                <span data-testid="context-cc-overdue" style={{ background: "#FEE2E2", color: "#B91C1C", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>Overdue: <b>{cc.overdue_tasks?.length || 0}</b></span>
                <span data-testid="context-cc-highpri" style={{ background: "#FEF3C7", color: "#92400E", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>High priority: <b>{cc.high_priority_items ?? 0}</b></span>
              </div>
              {(cc.top_items || []).slice(0, 5).map((it, i) => (
                <div key={i} style={{ fontSize: 12, color: "#374151", padding: "4px 0", borderTop: i === 0 ? "1px solid #F1F5F9" : "none" }}>
                  <span style={{ color: "#9CA3AF" }}>{it.department}</span> · {it.title} <span style={{ color: "#9CA3AF" }}>({it.due_timeline})</span>
                </div>
              ))}
              {(!cc.top_items || cc.top_items.length === 0) && (
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>No open Command Centre items right now.</div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button data-testid="open-inject-task-form-btn" onClick={() => setView("form")}
                  style={{ ...btn, background: NAVY, color: "white" }}>
                  Inject Task into Command Centre
                </button>
              </div>
            </div>
          )}

          {!loading && view === "form" && !result && (
            <div data-testid="inject-task-form">
              <label style={label}>Task title *</label>
              <input data-testid="inject-task-title" value={form.title} onChange={(e) => setField("title", e.target.value)}
                placeholder="e.g. Build South India Distributor Pipeline" style={{ ...input, marginBottom: 12 }} />

              <label style={label}>Task description *</label>
              <textarea data-testid="inject-task-description" value={form.description} onChange={(e) => setField("description", e.target.value)}
                placeholder="What exactly should the founder/team do?" style={{ ...input, height: 64, resize: "vertical", marginBottom: 12 }} />

              <label style={label}>Business objective / why</label>
              <textarea data-testid="inject-task-objective" value={form.businessObjective} onChange={(e) => setField("businessObjective", e.target.value)}
                placeholder="Why does this matter to this company specifically?" style={{ ...input, height: 48, resize: "vertical", marginBottom: 12 }} />

              <label style={label}>Expected outcome</label>
              <textarea data-testid="inject-task-outcome" value={form.expectedOutcome} onChange={(e) => setField("expectedOutcome", e.target.value)}
                placeholder="What does success look like?" style={{ ...input, height: 48, resize: "vertical", marginBottom: 12 }} />

              <label style={label}>Task From *</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {Object.keys(ROLE_COLORS).map((r) => (
                  <button key={r} type="button" data-testid={`inject-task-role-${r}`}
                    onClick={() => setField("executiveRole", r)}
                    style={{
                      ...btn, flex: 1,
                      background: form.executiveRole === r ? ROLE_COLORS[r].fg : ROLE_COLORS[r].bg,
                      color: form.executiveRole === r ? "white" : ROLE_COLORS[r].fg,
                      border: `1px solid ${ROLE_COLORS[r].fg}`,
                    }}>
                    {r}
                  </button>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={label}>Priority</label>
                  <select data-testid="inject-task-priority" value={form.priority} onChange={(e) => setField("priority", Number(e.target.value))} style={input}>
                    {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Due date</label>
                  <input data-testid="inject-task-due-date" type="date" value={form.dueDate} onChange={(e) => setField("dueDate", e.target.value)} style={input} />
                </div>
              </div>

              <label style={label}>Internal expert note (never shown to customer)</label>
              <textarea data-testid="inject-task-internal-note" value={form.internalNote} onChange={(e) => setField("internalNote", e.target.value)}
                placeholder="Internal context for the C-ORE team only…" style={{ ...input, height: 48, resize: "vertical", marginBottom: 12 }} />

              {duplicates && duplicates.length > 0 && (
                <div data-testid="inject-task-duplicate-warning" style={{ background: "#FEF3C7", color: "#92400E", fontSize: 12.5, padding: "10px 12px", borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, marginBottom: 4 }}>
                    <AlertTriangle size={13} /> An active task with a similar objective already exists for this customer.
                  </div>
                  {duplicates.map((d) => <div key={d.id} style={{ marginLeft: 19 }}>• {d.title} ({d.status})</div>)}
                </div>
              )}

              {err && <div data-testid="inject-task-error" style={{ background: "#FEE2E2", color: "#B91C1C", fontSize: 12, padding: "8px 10px", borderRadius: 8, marginBottom: 12 }}>{err}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
                <button data-testid="inject-task-cancel-btn" onClick={() => setView("context")} disabled={busy}
                  style={{ ...btn, background: "white", color: "#374151", border: "1px solid #D1D5DB" }}>Cancel</button>
                {duplicates && duplicates.length > 0 ? (
                  <button data-testid="inject-task-force-btn" onClick={() => submit(true)} disabled={busy}
                    style={{ ...btn, background: "#B45309", color: "white", opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Injecting…" : "Continue anyway"}
                  </button>
                ) : (
                  <button data-testid="inject-task-submit-btn" onClick={() => submit(false)} disabled={busy}
                    style={{ ...btn, background: NAVY, color: "white", opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Injecting…" : "Inject into Command Centre"}
                  </button>
                )}
              </div>
            </div>
          )}

          {result && (
            <div data-testid="inject-task-success" style={{ textAlign: "center", padding: "12px 0" }}>
              <CheckCircle2 size={28} color="#047857" />
              <div style={{ fontWeight: 700, color: "#0F1B2D", marginTop: 8 }}>
                Task successfully added to {name}'s Command Centre.
              </div>
              <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 6 }}>
                Task From: <b>{form.executiveRole}</b> · {form.title}
              </div>
              <button data-testid="inject-task-done-btn" onClick={onClose}
                style={{ ...btn, background: NAVY, color: "white", marginTop: 16 }}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
